/**
 * Tanzeen+ Visual Share Modal
 * ===========================
 * UNIFIED TEMPLATE - Full-Content Rendering Engine UI Component
 * 
 * Features:
 * - Auto-sized cards using height: fit-content
 * - Always displays Arabic + Transliteration + Translation for Duas/Hadiths
 * - Multi-slide carousel preview (1/N, 2/N...)
 * - Download all slides as PNG
 * - Native share integration
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Download, 
  Share2, 
  ChevronLeft, 
  ChevronRight, 
  Loader2,
  Image as ImageIcon
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Slide Preview Component - Updated for Tanzeen Engine
 */
const SlidePreview = ({ slideData, isActive }) => {
  // Support both old base64 format and new imageUrl format
  const imageSrc = slideData?.imageUrl || slideData?.base64;
  const slideNumber = slideData?.slideNumber || slideData?.slide_number || 1;
  
  if (!imageSrc) return null;
  
  return (
    <div 
      className={`flex-shrink-0 w-full transition-opacity duration-300 ${
        isActive ? 'opacity-100' : 'opacity-50'
      }`}
    >
      <img 
        src={imageSrc}
        alt={`Slide ${slideNumber}`}
        className="w-full h-auto rounded-lg shadow-lg"
        data-testid={`slide-preview-${slideNumber}`}
      />
    </div>
  );
};

/**
 * Visual Share Modal
 */
export const VisualShareModal = ({
  isOpen,
  onClose,
  contentType = 'dua', // 'dua', 'generated_dua', 'hadith', 'story'
  title,
  arabicText,
  translationText,
  transliterationText,
  reference,
}) => {
  const { t, i18n } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  // Single-attempt guard: the previous code re-fired generateCards() every time
  // isGenerating flipped from true->false in the finally, creating an infinite
  // 60s retry loop that spammed "Failed to generate visual cards" toasts. The
  // guard ensures at most one automatic attempt per modal open; the user can
  // explicitly retry via the Retry button in the error state.
  const [attempted, setAttempted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [usage, setUsage] = useState(null);
  
  // UNIFIED TEMPLATE: Settings toggle removed - always show all content layers
  
  // Generate visual cards using TANZEEN ENGINE - UNIFIED TEMPLATE
  const generateCards = useCallback(async () => {
    if (!arabicText && !translationText) {
      toast.error('No content to share');
      return;
    }
    
    setIsGenerating(true);
    setErrorMessage(null);
    setAttempted(true);
    
    // Explicit client-side timeout. Without this the Android WebView waits its
    // default ~60s before rejecting, which is what produced the observed
    // "spinner for a minute" symptom. 25s is enough for a healthy render and
    // short enough to surface production failure quickly.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 25000);
    
    try {
      const requestBody = {
        content_type: contentType,
        title: title || 'Tanzeen+',
        content: translationText || '',
        arabic_text: arabicText || null,
        transliteration: transliterationText || null,
        reference: reference || '',
        language: i18n.language || 'en',
        user_id: localStorage.getItem('quranplus_user_id') || undefined,
      };
      
      const response = await fetch(`${API}/api/tanzeen-engine/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail?.message || `Server responded with ${response.status}`);
      }
      
      if (data.success && data.image_urls && data.image_urls.length > 0) {
        const generatedSlides = data.image_urls.map((url, index) => ({
          imageUrl: `${API}${url}`,
          slideNumber: index + 1,
          totalSlides: data.total_slides
        }));
        setSlides(generatedSlides);
        setCurrentSlide(0);
        setUsage(data.usage || null);
        toast.success(`Generated ${data.total_slides} card${data.total_slides > 1 ? 's' : ''}`);
      } else {
        throw new Error(data.message || 'Generation failed');
      }
    } catch (error) {
      console.error('Card generation error:', error);
      const msg = error.name === 'AbortError'
        ? 'Card generation timed out. Please try again.'
        : 'Failed to generate visual card.';
      setErrorMessage(msg);
      // Single toast, only shown here. No auto-retry loop -> no duplicate toasts.
    } finally {
      clearTimeout(timeoutId);
      setIsGenerating(false);
    }
  }, [arabicText, translationText, transliterationText, contentType, title, reference, i18n.language]);
  
  // Auto-generate on open — but ONLY ONCE per open. The guard `attempted` blocks
  // the effect from re-firing when isGenerating flips back to false on failure.
  useEffect(() => {
    if (isOpen && slides.length === 0 && !isGenerating && !attempted) {
      generateCards();
    }
    // generateCards intentionally NOT in deps: it changes on every render due
    // to its own dep list (props + i18n.language) and would cause spurious
    // re-entries. `attempted` is the true single-shot guard.
  }, [isOpen, slides.length, isGenerating, attempted]);
  
  // Reset on close so the next open starts fresh
  useEffect(() => {
    if (!isOpen) {
      setSlides([]);
      setCurrentSlide(0);
      setAttempted(false);
      setErrorMessage(null);
      setUsage(null);
    }
  }, [isOpen]);
  
  const retryGeneration = () => {
    setErrorMessage(null);
    setAttempted(false); // next effect run will fire generateCards once
  };
  
  // Navigation
  const goToPrevSlide = () => {
    setCurrentSlide(prev => Math.max(0, prev - 1));
  };
  
  const goToNextSlide = () => {
    setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1));
  };
  
  // Download single slide - Updated for Tanzeen Engine.
  //
  // On WEB we can rely on the anchor-tag `download` attribute — the browser
  // resolves the URL and drops the file into the user's Downloads folder.
  //
  // On CAPACITOR ANDROID the same anchor-click does NOTHING inside the WebView
  // (there is no browser chrome to route the intent through the system
  // DownloadManager, no download-listener attached to the WebView, no scoped
  // Downloads permission granted). The `<a download>` fallback silently
  // fails, the outer try/catch hits the `error` branch, and the user sees
  // "Download Failed" — that's bug #6C.
  //
  // Fix: on native we do NOT try to write to the public Downloads folder
  // (that would need WRITE_EXTERNAL_STORAGE on API 28- or the SAF picker on
  // API 29+, neither of which we want to demand). Instead we write the PNG
  // to Filesystem.Cache (no permission needed) and open the native share
  // sheet with that file URI attached. Android users then pick their own
  // destination — Files, Drive, Photos, WhatsApp — from the share sheet.
  // This is the platform-idiomatic "save from app" flow and matches what
  // the "Share" button already does; the Download button now just becomes
  // a synonym on native (no more "Download Failed" toast).
  const downloadSlide = useCallback(async (slideData) => {
    try {
      const imageSrc = slideData.imageUrl || slideData.base64;
      const slideNumber = slideData.slideNumber || slideData.slide_number || 1;

      if (!imageSrc) {
        toast.error('No image to download');
        return;
      }

      // Resolve the image into a Blob regardless of source (URL / data URI).
      let blob;
      if (slideData.imageUrl) {
        const response = await fetch(imageSrc);
        blob = await response.blob();
      } else {
        // base64 data URI
        const commaIdx = String(imageSrc).indexOf(',');
        const base64Data = commaIdx >= 0 ? String(imageSrc).slice(commaIdx + 1) : String(imageSrc);
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        blob = new Blob([new Uint8Array(byteNumbers)], { type: 'image/png' });
      }

      // NATIVE: Filesystem.Cache + Share.share({ url }). No <a> click, no
      // "Download Failed". Any failure here rethrows into the outer catch.
      if (Capacitor.isNativePlatform()) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(reader.error);
          reader.onload = () => {
            const dataUrl = reader.result || '';
            const commaIdx = String(dataUrl).indexOf(',');
            resolve(commaIdx >= 0 ? String(dataUrl).slice(commaIdx + 1) : '');
          };
          reader.readAsDataURL(blob);
        });

        const fileName = `tanzeen_${contentType}_${slideNumber}_${Date.now()}.png`;
        const written = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
        });

        await Share.share({
          title: 'Tanzeen +',
          text: 'Shared by Tanzeen +',
          url: written.uri,
          dialogTitle: 'Save or share',
        });
        toast.success('Saved to share sheet');
        return;
      }

      // WEB: anchor-tag download (unchanged path — this is what worked
      // before and continues to work in every desktop / mobile web browser).
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tanzeen_${contentType}_${slideNumber}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Downloaded!');
    } catch (error) {
      if (error?.name === 'AbortError') return; // user dismissed the native share sheet — not a failure
      console.error('[VisualShare] download error:', error);
      toast.error('Download failed');
    }
  }, [contentType]);
  
  // Download all slides
  const downloadAllSlides = useCallback(async () => {
    for (const slide of slides) {
      await downloadSlide(slide);
      // Small delay between downloads
      await new Promise(r => setTimeout(r, 300));
    }
    toast.success(`Downloaded ${slides.length} slide${slides.length > 1 ? 's' : ''}`);
  }, [slides, downloadSlide]);
  
  // Share slides - Updated for Tanzeen Engine (URL-based images)
  const shareSlides = useCallback(async () => {
    if (slides.length === 0) return;
    
    const currentSlideData = slides[currentSlide];
    
    // BRANDING: Share text is ALWAYS "Shared by Tanzeen +" - no dynamic title
    const shareText = "Shared by Tanzeen +";
    const shareTitle = "Tanzeen +";
    
    try {
      // Get the image URL (Tanzeen Engine returns imageUrl, not base64)
      const imageUrl = currentSlideData.imageUrl || currentSlideData.base64;
      
      if (!imageUrl) {
        toast.error('No image to share');
        return;
      }
      
      let blob;
      
      // If it's a URL, fetch the image first
      if (imageUrl.startsWith('/') || imageUrl.startsWith('http')) {
        const response = await fetch(imageUrl);
        blob = await response.blob();
      } else if (imageUrl.startsWith('data:')) {
        // Convert base64 to blob (legacy support)
        const base64Data = imageUrl.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: 'image/png' });
      } else {
        toast.error('Invalid image format');
        return;
      }
      
      // Try native sharing — must attach the generated card image as a
      // real file, otherwise Android just shares the title/text ("Tanzeen").
      // Prior to 2026-09 this path called `Share.share({ title, text })`
      // with no `url`, which was the exact cause of "share only says the
      // app name" and the "Download Failed" fallback toast on Android.
      if (Capacitor.isNativePlatform()) {
        try {
          // Serialize the blob to a base64 string so we can hand it to
          // Capacitor Filesystem (Filesystem.writeFile needs base64 data).
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
              const dataUrl = reader.result || '';
              const commaIdx = String(dataUrl).indexOf(',');
              resolve(commaIdx >= 0 ? String(dataUrl).slice(commaIdx + 1) : '');
            };
            reader.readAsDataURL(blob);
          });

          const fileName = `tanzeen_${contentType}_${Date.now()}.png`;
          const written = await Filesystem.writeFile({
            path: fileName,
            data: base64,
            directory: Directory.Cache,
          });

          await Share.share({
            title: shareTitle,
            text: shareText,
            url: written.uri,
            dialogTitle: shareTitle,
          });
          toast.success('Shared!');
          return;
        } catch (nativeErr) {
          if (nativeErr?.name === 'AbortError' || /cancel/i.test(nativeErr?.message || '')) {
            // User dismissed the native share sheet — not a failure.
            return;
          }
          // On Capacitor Android WebView, `navigator.share` typically does
          // NOT exist and the "Web Share fallback" block below would just
          // recurse into `downloadSlide` which itself calls Share.share
          // again → infinite fail loop. Surface a real toast and stop.
          console.warn('[Share] Native file share failed:', nativeErr?.message);
          toast.error('Share failed. Please try again.');
          return;
        }
      }
      
      // Web Share API with file
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], `tanzeen_${contentType}.png`, { type: 'image/png' });
        
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            files: [file],
          });
          toast.success('Shared!');
          return;
        }
      }
      
      // Fallback to text share (web only — native branch returned above)
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
        });
        toast.success('Shared!');
        return;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Share error:', error);
    }
    
    // Final fallback (WEB only) - trigger the anchor-tag download so the
    // user still gets the PNG. Native never reaches here — see the early
    // return in the Capacitor branch above.
    if (!Capacitor.isNativePlatform()) {
      downloadSlide(currentSlideData);
    }
  }, [slides, currentSlide, title, contentType, downloadSlide]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="visual-share-modal"
    >
      <div 
        className="w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - UNIFIED TEMPLATE: No settings toggle */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-primary" />
            <span className="font-semibold">
              {t('share.visualShare', 'Visual Share')}
            </span>
            {slides.length > 1 && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {currentSlide + 1}/{slides.length}
              </span>
            )}
            {usage && (
              <span className="text-xs text-muted-foreground" data-testid="share-card-usage-indicator">
                {usage.current_usage}/{usage.daily_limit} today
              </span>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Slide Preview */}
        <div className="flex-1 overflow-hidden p-4 bg-muted/10">
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center h-64" data-testid="visual-share-loading">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">
                {t('share.generating', 'Generating visual cards...')}
              </p>
            </div>
          ) : errorMessage ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 px-6 text-center" data-testid="visual-share-error">
              <p className="text-sm text-foreground">{errorMessage}</p>
              <div className="flex gap-2">
                <Button onClick={retryGeneration} variant="default" size="sm" data-testid="visual-share-retry">
                  {t('common.retry', 'Retry')}
                </Button>
                <Button onClick={onClose} variant="outline" size="sm">
                  {t('common.close', 'Close')}
                </Button>
              </div>
            </div>
          ) : slides.length > 0 ? (
            <div className="relative">
              <SlidePreview 
                slideData={slides[currentSlide]} 
                isActive={true}
              />
              
              {/* Navigation Arrows */}
              {slides.length > 1 && (
                <>
                  <button
                    onClick={goToPrevSlide}
                    disabled={currentSlide === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white disabled:opacity-30 transition-opacity"
                    data-testid="prev-slide-btn"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={goToNextSlide}
                    disabled={currentSlide === slides.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white disabled:opacity-30 transition-opacity"
                    data-testid="next-slide-btn"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              
              {/* Slide Indicators */}
              {slides.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-3">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentSlide 
                          ? 'bg-primary w-4' 
                          : 'bg-muted-foreground/30'
                      }`}
                      data-testid={`slide-indicator-${idx}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <ImageIcon className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                {t('share.noPreview', 'No preview available')}
              </p>
              <Button 
                onClick={generateCards} 
                variant="outline" 
                size="sm" 
                className="mt-3"
              >
                {t('share.retry', 'Retry')}
              </Button>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="px-4 pb-4 pt-2 space-y-2 border-t border-border">
          <div className="flex gap-2">
            <Button
              onClick={shareSlides}
              disabled={isGenerating || slides.length === 0}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              data-testid="share-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              {t('share.share', 'Share')}
            </Button>
            <Button
              onClick={() => downloadSlide(slides[currentSlide])}
              disabled={isGenerating || slides.length === 0}
              variant="outline"
              className="flex-1"
              data-testid="download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('share.download', 'Download')}
            </Button>
          </div>
          
          {slides.length > 1 && (
            <Button
              onClick={downloadAllSlides}
              disabled={isGenerating}
              variant="ghost"
              className="w-full"
              data-testid="download-all-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('share.downloadAll', 'Download All')} ({slides.length})
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualShareModal;
